import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Rocket,
  Target,
  Calendar,
  Map,
  Settings,
  TestTube,
  ArrowRightLeft,
  Shield,
  Users,
  Award,
  // ChevronRight,
} from "lucide-react";
import Footer from "../../../components/Footer";

/* ---------------- DATA DEFINITIONS ---------------- */

const HERO = {
  title: "Design And Deploy",
  subtitle:
    "End-to-end security architecture built, tested, and delivered by experts.",
  description:
    "From first workshop to full production rollout we handle everything.",
  cta: "Start Your Deployment",
};

const WHY_EXISTS = {
  headline: "Security projects don't fail at purchase.",
  subheadline: "They fail at deployment.",
  description:
    "Most organizations invest in powerful security platforms but struggle to design, configure, and deploy them correctly. The result? Delays, user disruption, and tools that never reach their full potential.",
  mission:
    "Our Design & Deploy service ensures your security platform is built the right way from day one.",
};

const METHODOLOGY_STEPS = [
  {
    number: "01",
    title: "Project Initiation",
    subtitle: "We start with clarity.",
    description:
      "We align stakeholders, define success metrics, and deeply understand your environment, risks, and business priorities. Every successful deployment begins with shared goals and a clear vision.",
    icon: Target,
    color: "from-emerald-600 to-green-400",
  },
  {
    number: "02",
    title: "Planning",
    subtitle: "A roadmap before a single change.",
    description:
      "We create a detailed implementation plan covering timelines, dependencies, change management, and rollback strategies. Nothing is left to guesswork.",
    icon: Calendar,
    color: "from-green-500 to-emerald-400",
  },
  {
    number: "03",
    title: "Design",
    subtitle: "Architecture built for your reality.",
    description:
      "We design a security architecture tailored to your users, applications, cloud, and on-prem environments balancing protection, performance, and user experience.",
    icon: Map,
    color: "from-emerald-500 to-teal-400",
  },
  {
    number: "04",
    title: "Configuration",
    subtitle: "Best-practice setup from day one.",
    description:
      "Your platform is configured using proven best practices and real-world deployment experience not default settings.",
    icon: Settings,
    color: "from-teal-500 to-emerald-400",
  },
  {
    number: "05",
    title: "Testing",
    subtitle: "Nothing goes live without proof.",
    description:
      "We validate performance, functionality, failover, and security posture in controlled environments before production rollout.",
    icon: TestTube,
    color: "from-emerald-600 to-green-500",
  },
  {
    number: "06",
    title: "Migration",
    subtitle: "Seamless transition. Zero disruption.",
    description:
      "Users, traffic, and policies are migrated using a phased, controlled approach designed to minimize risk and downtime.",
    icon: ArrowRightLeft,
    color: "from-green-500 to-lime-400",
  },
  {
    number: "07",
    title: "Deployment",
    subtitle: "Production ready. Business protected.",
    description:
      "We finalize production rollout, validate success criteria, provide documentation, and transfer knowledge to your team. Your platform goes live with confidence.",
    icon: Rocket,
    color: "from-lime-500 to-emerald-400",
  },
];

const DIFFERENTIATORS = [
  {
    title: "Real-world expertise",
    description:
      "Delivered by engineers who deploy and operate security platforms every day.",
    icon: Users,
  },
  {
    title: "Business-first approach",
    description: "Security that enables productivity not blocks it.",
    icon: Shield,
  },
  {
    title: "Proven methodology",
    description:
      "A structured, repeatable framework that reduces risk and accelerates outcomes.",
    icon: Award,
  },
];

/* ---------------- ANIMATION VARIANTS ---------------- */

const FADE_UP = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const STAGGER_CONTAINER = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const SCALE_IN = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } },
};

/* ---------------- SUB-COMPONENTS ---------------- */

const MethodologyCard = ({ step, index }) => (
  <motion.div
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-50px" }}
    variants={FADE_UP}
    className="relative group"
  >
    {index < METHODOLOGY_STEPS.length - 1 && (
      <div className="absolute left-8 top-24 w-0.5 h-24 bg-gradient-to-b from-emerald-100 to-transparent hidden lg:block" />
    )}

    <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 items-start p-6 lg:p-8 rounded-3xl bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
      <div className="flex items-center gap-4 lg:flex-col lg:items-center lg:gap-3 shrink-0">
        <div
          className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}
        >
          <step.icon className="text-white" size={28} />
        </div>
        <span className="text-3xl font-black text-slate-100 lg:text-4xl">
          {step.number}
        </span>
      </div>

      <div className="flex-1">
        <h3 className="text-2xl font-bold text-slate-900 mb-2 flex items-center gap-3">
          {step.title}
        </h3>
        <p className="text-emerald-600 font-semibold mb-3 text-sm uppercase tracking-wider">
          {step.subtitle}
        </p>
        <p className="text-slate-600 leading-relaxed text-lg">
          {step.description}
        </p>
      </div>
    </div>
  </motion.div>
);

const DifferentiatorCard = ({ item }) => (
  <motion.div
    variants={SCALE_IN}
    className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm hover:shadow-lg transition-all duration-300 group"
  >
    <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-emerald-600 transition-colors duration-300">
      <item.icon
        className="text-emerald-600 group-hover:text-white transition-colors duration-300"
        size={28}
      />
    </div>
    <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
    <p className="text-slate-600 leading-relaxed">{item.description}</p>
  </motion.div>
);

/* ---------------- MAIN COMPONENT ---------------- */

export default function DeployAndDesign() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Security Design & Deployment Services | Synthera Security</title>

        <meta
          name="description"
          content="End-to-end security architecture design and deployment services. Synthera Security helps organizations plan, configure, test, migrate, and deploy cybersecurity platforms successfully."
        />

        <meta
          name="keywords"
          content="Security Deployment, Security Architecture, Cybersecurity Design, Security Implementation, Security Consulting, Design and Deploy, Synthera Security"
        />

        <meta name="robots" content="index, follow" />

        <meta
          property="og:title"
          content="Security Design & Deployment Services | Synthera Security"
        />

        <meta
          property="og:description"
          content="From planning and architecture to testing and deployment, Synthera Security delivers successful cybersecurity implementations."
        />

        <meta
          property="og:url"
          content="https://www.syntherasecurity.com/services/design-and-deploy"
        />

        <meta property="og:type" content="website" />

        <link
          rel="canonical"
          href="https://www.syntherasecurity.com/services/design-and-deploy"
        />
      </Helmet>

      <div className="min-h-screen bg-slate-50 font-sans text-slate-900 overflow-x-hidden">
        {/* --- HERO --- */}
        {/* ================= HERO SECTION ================= */}
        <section className="bg-[#032d28] pt-32 pb-24 px-6 relative overflow-hidden">
          {/* Grid Background */}
          <div
            className="absolute inset-0 opacity-10 
          bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),
          linear-gradient(to_bottom,#80808012_1px,transparent_1px)] 
          bg-[size:40px_40px]"
          />

          {/* Animated Orbs */}
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.3, 0.2] }}
            transition={{ duration: 8, repeat: Infinity }}
            className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl pointer-events-none"
          />

          <div className="container mx-auto max-w-5xl text-center relative z-10">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={STAGGER_CONTAINER}
            >
              {/* Main Heading */}
              <motion.h1
                variants={FADE_UP}
                className="text-white text-4xl md:text-7xl font-bold leading-[1.1] mb-8 tracking-tight"
              >
                {HERO.title}
              </motion.h1>

              {/* Green Accent Bar */}
              <motion.div
                variants={FADE_UP}
                className="w-24 h-1 bg-[#24ff81] mx-auto mb-8"
              />

              {/* Subtitle - Bold and large like Page 1 */}
              <motion.p
                variants={FADE_UP}
                className="text-emerald-100/90 text-2xl md:text-3xl leading-snug max-w-4xl mx-auto font-bold mb-4"
              >
                {HERO.subtitle}
              </motion.p>

              {/* Description - Smaller so it doesn't eat too much space */}
              <motion.p
                variants={FADE_UP}
                className="text-white/60 text-base md:text-lg max-w-2xl mx-auto mb-10"
              >
                {HERO.description}
              </motion.p>

              {/* CTA Buttons */}
              <motion.div
                variants={FADE_UP}
                className="flex flex-col sm:flex-row gap-6 justify-center items-center"
              >
                <Link
                  to="/Contact"
                  className="inline-flex items-center gap-3 py-2.5 px-7 bg-[#00C08B] text-slate-900 rounded-full font-extrabold text-lg hover:bg-white transition-all duration-300 shadow-lg no-underline"
                >
                  {HERO.cta} →
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* --- WHY THIS SERVICE EXISTS --- */}
        <section className="py-24 lg:py-32 px-6 bg-white relative overflow-hidden">
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={STAGGER_CONTAINER}
              >
                <motion.span
                  variants={FADE_UP}
                  className="text-emerald-600 font-bold tracking-widest text-sm uppercase mb-4 block"
                >
                  The Problem
                </motion.span>

                <motion.h2
                  variants={FADE_UP}
                  className="text-4xl lg:text-5xl font-black text-slate-900 mb-6 leading-tight"
                >
                  {WHY_EXISTS.headline}
                  <span className="text-emerald-600 block mt-2">
                    {WHY_EXISTS.subheadline}
                  </span>
                </motion.h2>

                <motion.p
                  variants={FADE_UP}
                  className="text-lg text-slate-600 mb-6 leading-relaxed"
                >
                  {WHY_EXISTS.description}
                </motion.p>

                <motion.div
                  variants={FADE_UP}
                  className="p-6 bg-emerald-50 rounded-2xl border-l-4 border-emerald-600"
                >
                  <p className="text-emerald-900 font-medium text-lg">
                    {WHY_EXISTS.mission}
                  </p>
                </motion.div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative"
              >
                <div className="relative z-10 bg-slate-900 rounded-3xl p-8 shadow-2xl">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  </div>
                  <div className="space-y-4">
                    <div className="h-4 bg-slate-700 rounded w-3/4 animate-pulse" />
                    <div className="h-4 bg-slate-700 rounded w-full animate-pulse" />
                    <div className="h-32 bg-slate-800 rounded-lg mt-6 flex items-center justify-center border border-slate-700">
                      <div className="text-center">
                        <div className="text-red-400 text-4xl mb-2">⚠️</div>
                        <div className="text-slate-400 text-sm">
                          Deployment Failed
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* --- METHODOLOGY --- */}
        <section className="py-24 lg:py-32 px-6 bg-slate-50 relative">
          <div className="container mx-auto max-w-4xl">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={STAGGER_CONTAINER}
              className="text-center mb-20"
            >
              <motion.span
                variants={FADE_UP}
                className="text-emerald-600 font-bold tracking-widest text-sm uppercase mb-4 block"
              >
                Our Process
              </motion.span>
              <motion.h2
                variants={FADE_UP}
                className="text-4xl lg:text-5xl font-black text-slate-900 mb-6"
              >
                Our Deployment Methodology
              </motion.h2>
            </motion.div>

            <div className="space-y-6">
              {METHODOLOGY_STEPS.map((step, index) => (
                <MethodologyCard key={step.number} step={step} index={index} />
              ))}
            </div>
          </div>
        </section>

        {/* --- WHAT MAKES US DIFFERENT --- */}
        <section className="py-24 lg:py-32 px-6 bg-[#032d28] relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-emerald-900/30 via-[#032d28] to-[#032d28]" />

          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="text-center mb-16">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-4xl lg:text-5xl font-black text-white mb-6"
              >
                What Makes Us Different
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-2xl text-emerald-400 font-light"
              >
                Deployment is where most projects fail.
                <span className="block text-white font-bold mt-2">
                  It's where we excel.
                </span>
              </motion.p>
            </div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={STAGGER_CONTAINER}
              className="grid md:grid-cols-3 gap-8"
            >
              {DIFFERENTIATORS.map((item) => (
                <DifferentiatorCard key={item.title} item={item} />
              ))}
            </motion.div>
          </div>
        </section>

        {/* --- FINAL CTA --- */}
        {/* <section className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-[#032d28] rounded-[2.5rem] p-12 lg:p-16 shadow-2xl relative overflow-hidden"
          >
            <h2 className="text-3xl lg:text-5xl font-black text-white mb-6 relative z-10">
              Ready to deploy with confidence?
            </h2>
            <p className="text-emerald-50 text-lg mb-10 max-w-2xl mx-auto relative z-10">
              Let's discuss how our Design & Deploy service can transform your
              security infrastructure.
            </p>
            <Link
              to="/Contact"
              className="inline-flex items-center gap-3 py-2.5 px-7 bg-[#00C08B] text-slate-900 rounded-full font-extrabold text-lg hover:bg-white transition-all duration-300 shadow-lg no-underline"
            >
              Start Your Project
              <ChevronRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section> */}

        <Footer />
      </div>
    </>
  );
}
